import { useEffect, useRef, useState } from "react"
import type { Message } from "../types"

interface UseNewMessageLineProps {
    messages: Message[]
    numUnread: number
    isScrolledUp: boolean
    isNearBottom: boolean
    initialUnread: number
    hasReceivedNewMessages: boolean
}

interface UseNewMessageLineResult {
    newMessageLineIndex: number | null
    isNewMessageLineVisible: boolean
    setNewMessageLineIndex: (index: number | null) => void
    setIsNewMessageLineVisible: (visible: boolean) => void
}

export function useNewMessageLine({
    messages,
    numUnread,
    isScrolledUp,
    isNearBottom,
    initialUnread,
    hasReceivedNewMessages,
}: UseNewMessageLineProps): UseNewMessageLineResult {
    const [newMessageLineIndex, setNewMessageLineIndex] = useState<
        number | null
    >(null)
    const [isNewMessageLineVisible, setIsNewMessageLineVisible] =
        useState(false)
    const wasAtBottom = useRef(true)
    const initialPositionId = useRef<string | null>(null)

    // Handle new message line positioning
    useEffect(() => {
        if (messages.length === 0) return

        let targetIndex: number | null = null

        // Case 1: New messages while scrolled up
        if (
            hasReceivedNewMessages &&
            wasAtBottom.current &&
            isScrolledUp &&
            numUnread > 0
        ) {
            targetIndex = messages.length - numUnread
            if (targetIndex !== -1) {
                setNewMessageLineIndex(targetIndex)
                setIsNewMessageLineVisible(true)
            }
        }
        // Case 2: Initial unread messages
        else if (initialUnread > 0 && !hasReceivedNewMessages) {
            targetIndex = initialPositionId.current
                ? messages.findIndex(
                      (msg) => msg.i === initialPositionId.current,
                  )
                : messages.length - initialUnread

            if (targetIndex !== -1) {
                setNewMessageLineIndex(targetIndex)
                setIsNewMessageLineVisible(true)

                if (!initialPositionId.current) {
                    initialPositionId.current = messages[targetIndex]?.i ?? null
                }
            }
        }
    }, [
        messages,
        numUnread,
        isScrolledUp,
        initialUnread,
        hasReceivedNewMessages,
    ])

    useEffect(() => {
        if (isNearBottom) {
            wasAtBottom.current = true
        }
    }, [isNearBottom])

    return {
        newMessageLineIndex,
        isNewMessageLineVisible,
        setNewMessageLineIndex,
        setIsNewMessageLineVisible,
    }
}
